import { ReactElement, useMemo, useEffect } from 'react';
import { Stack, Typography } from '@mui/material';

import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { useToggle } from '~hooks/useToggle';
import {
  PlanFeaturesTypes,
  PreferencesWhatsappModeType,
  PreferencesWhatsappOwnLineStatusType,
} from '~globals/types/enums';

import DialogLinkWhatsappOwnLine from '../DialogLinkWhatsappOwnLine';

import {
  WhatsappTypeSelectorProps,
  WhatsappTypeSelectorOptionStatusChipType,
} from './types';
import WhatsappTypeSelectorOption from './SelectorOption';

const WhatsappTypeSelector = ({
  mode,
  ownLineStatus,
}: WhatsappTypeSelectorProps): ReactElement => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const whatsappLiteStatus =
    useMemo<WhatsappTypeSelectorOptionStatusChipType>(() => {
      if (!hasAccessToFeature(PlanFeaturesTypes.NotifWhatsapp)) {
        return 'not-available';
      }

      return mode === PreferencesWhatsappModeType.Lite
        ? 'connected'
        : 'disconnected';
    }, [hasAccessToFeature, mode]);

  const whatsappOwnLineStatus =
    useMemo<WhatsappTypeSelectorOptionStatusChipType>(() => {
      if (!hasAccessToFeature(PlanFeaturesTypes.NotifWhatappBusiness)) {
        return 'not-available';
      }

      return mode === PreferencesWhatsappModeType.OwnLine &&
        ownLineStatus === PreferencesWhatsappOwnLineStatusType.Connected
        ? 'connected'
        : 'disconnected';
    }, [hasAccessToFeature, mode, ownLineStatus]);

  const [openDialogLinkWhatsappOwnLine, toggleDialogLinkWhatsappOwnLine] =
    useToggle(false);

  useEffect(() => {
    if (ownLineStatus === PreferencesWhatsappOwnLineStatusType.Connected) {
      toggleDialogLinkWhatsappOwnLine(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownLineStatus]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" fontWeight="medium">
        Canal seleccionado para la comunicación
      </Typography>

      <Stack direction="row" spacing={2}>
        <WhatsappTypeSelectorOption
          status={whatsappLiteStatus}
          title="WhatsApp Lite"
          subTitle="Comunicación autogestionada"
          caption="No requiere linea propia"
        />

        <WhatsappTypeSelectorOption
          status={whatsappOwnLineStatus}
          title="WhatsApp Linea Propia"
          subTitle="Comunicación usando su propia marca personal"
          caption="Requiere linea propia"
          actionDisconnected={{
            label: 'Vincular línea',
            onClick: () => toggleDialogLinkWhatsappOwnLine(true),
          }}
        />
      </Stack>

      {openDialogLinkWhatsappOwnLine && (
        <DialogLinkWhatsappOwnLine
          open={openDialogLinkWhatsappOwnLine}
          onClose={() => toggleDialogLinkWhatsappOwnLine(false)}
        />
      )}
    </Stack>
  );
};

export default WhatsappTypeSelector;
